<template>
  <v-dialog 
    v-model="openMode" 
    max-width="900px" 
    content-class="listening-statistics-dialog"
    scrollable
  >
    <v-card class="dialog-card">

      <v-card-title class="dialog-header">
        <h2 class="dialog-title">Listening Statistics</h2>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDelete">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <v-card-text class="dialog-content">

        <v-data-table
          :headers="headers"
          :items="listeningArray"
          sort-by="date_listened"
          sort-desc
          :footer-props="{ 
            'items-per-page-options': [25, 50, 100], 
            'items-per-page-text': 'Rows per page:' 
          }"
          :mobile-breakpoint="0"
          class="listening-stats-table"
          no-data-text="No one has listened to the message yet"
        >
          <template v-for="header in headers"
            v-slot:[`header.${header.value}`]="{ header }">
            <span class="header-text">{{ header.text }}</span>
          </template>
          
          <template v-slot:item="{ item, index }">
            <tr class="table-row">
              <td class="text-center index-cell">{{ index + 1 }}</td>
              
              <td class="text-center">
                <div class="name-cell">
                  <v-avatar size="28" color="#0d2c6d" class="white--text ml-2">
                    {{ getInitials(item.name) }}
                  </v-avatar>
                  <span class="name-text">{{ item.name }}</span>
                </div>
              </td>
              
              <td class="text-center">
                <div class="phone-cell">
                  <v-icon small color="#0d2c6d" class="ml-1">mdi-phone</v-icon>
                  <span>{{ item.phone }}</span>
                </div>
              </td>
              
              <td class="text-center">{{ item.date_listened }}</td>
              
              <td class="text-center">
                <div class="time-cell">
                  <v-icon small color="#0d2c6d" class="ml-1">mdi-clock-outline</v-icon>
                  <span>{{ item.time_listened }}</span>
                </div>
              </td>
              
              <td class="text-center">
                <div class="percentage-cell">
                  <v-progress-linear
                    :value="parsePercentage(item.precentages)"
                    height="8"
                    rounded
                    :color="getProgressColor(parsePercentage(item.precentages))"
                    background-color="grey lighten-3"
                    class="percentage-progress"
                  ></v-progress-linear>
                  <span class="percentage-text">{{ item.precentages + '%' }}</span>
                </div>
              </td>
            </tr>
          </template>
          
          <template v-slot:no-data>
            <div class="empty-state">
              <v-icon size="64" color="grey lighten-1">mdi-headphones</v-icon>
              <p class="empty-text">No one has listened to the message yet</p>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
      
      <v-divider></v-divider>
      
      <v-card-actions class="dialog-actions">
        <v-spacer></v-spacer>
        <v-btn 
          color="#0d2c6d" 
          text 
          @click="closeDelete"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Utils from '@/util/utils'

export default {
  props: {
    listeningDetails: Array,
    recordDuration: String,
    value: { type: Boolean, default: false },
  },
  data: () => ({
    listeningArray: [],
    headers: [
      { text: '#', value: "index", sortable: false, width: '5%' },
      { text: 'Name', value: "name", width: '25%' },
      { text: 'Phone', align: 'center', value: 'phone', width: '15%' },
      { text: 'Date', value: 'date_listened', width: '15%' },
      { text: 'Listening Time', value: 'time_listened', width: '15%' },
      { text: 'Listening %', value: 'precentages', width: '25%' },
    ],
  }),
  methods: {
    closeDelete() {
      this.openMode = false;
    },
    
    formatDetails() {
      this.listeningArray = this.listeningArray.map((listener) => {
        return {
          ...listener,
          time_listened: this.formatDuration(listener.time_listened),
          precentages: Utils.getPercentage(listener.time_listened, this.recordDuration),
        }
      });
    },
    
    formatDuration(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const sec = seconds % 60;

      const paddedHours = hours.toString().padStart(2, '0');
      const paddedMinutes = minutes.toString().padStart(2, '0');
      const paddedSeconds = sec.toString().padStart(2, '0');

      return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
    },
    
    getInitials(name) {
      if (!name) return '';
      return name.split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    },
    
    formatPhone(phone) {
      if (!phone) return '';
      // Format phone number with hyphens (e.g., 050-1234567)
      if (phone.length === 10) {
        return `${phone.substring(0, 3)}-${phone.substring(3)}`;
      }
      return phone;
    },
    
    parsePercentage(percentStr) {
      console.log('Parsing percentage:', percentStr);
      
      if (!percentStr) return 0;
      else return percentStr
    },
    
    getProgressColor(percentage) {
      if (percentage < 30) return 'red';
      if (percentage < 70) return 'orange';
      return 'green';
    }
  },
  computed: {
    openMode: {
      get() {
        return this.value;
      },
      set(v) {
        return this.$emit('input', v);
      }
    },
  },
  created() {
    this.listeningArray = [...this.listeningDetails];
    this.formatDetails();
  },
}
</script>

<style scoped>
.listening-statistics-dialog {
  border-radius: 8px;
  overflow: hidden;
}

.dialog-card {
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  background-color: #f5f7fa;
  padding: 16px 24px;
  display: flex;
  align-items: center;
}

.dialog-title {
  color: #0d2c6d;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  font-family: 'Heebo', sans-serif;
}

.dialog-content {
  padding: 0;
  flex: 1;
}

.dialog-actions {
  padding: 12px 24px;
  background-color: #f5f7fa;
}

.listening-stats-table {
  width: 100%;
  border-radius: 0;
}

/* Style for table headers */
.listening-stats-table >>> th {
  background-color: #f5f7fa !important;
  height: 48px !important;
  text-align: center !important;
  font-family: 'Heebo', sans-serif !important;
  font-size: 14px;
  font-weight: 600 !important;
  color: #0d2c6d;
  border-bottom: 1px solid rgba(0,0,0,0.12) !important;
}

.header-text {
  font-weight: 600;
}

.table-row {
  height: 60px !important;
  transition: background-color 0.2s;
}

.table-row:hover {
  background-color: #f8f9fd !important;
}

/* Alternating row colors */
.listening-stats-table >>> tbody tr:nth-child(even) {
  background-color: rgba(0, 0, 0, 0.03);
}

.index-cell {
  font-weight: 500;
  color: #666;
}

.name-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.name-text {
  font-weight: 500;
  color: #333;
}

.phone-cell, .time-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
}

.percentage-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
}

.percentage-progress {
  width: 100%;
  margin-bottom: 4px;
}

.percentage-text {
  font-size: 12px;
  font-weight: 500;
  color: #555;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}

.empty-text {
  margin-top: 16px;
  font-size: 16px;
  color: #666;
  font-family: 'Heebo', sans-serif;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #0d2c6d;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #183677;
}
</style>